<template>
  <div class="single-matching-page">
    <!-- 页面标题 -->
    <div class="page-header">
      <h1 class="page-title">单次匹配</h1>
      <p class="page-subtitle">选择一对个体进行育种匹配</p>
    </div>

    <!-- 选择卡片区域 -->
    <div class="selection-cards">
      <!-- 公畜选择卡片 -->
      <div class="selection-card">
        <div class="card-header">
          <h3 class="card-title">
            <span class="gender-icon male">♂</span>
            选择公畜个体
          </h3>
          <a-button type="primary" class="select-btn" @click="openSelect(1)">
            <a-icon type="plus" />
            选择个体
          </a-button>
        </div>
        
        <div class="card-content">
          <!-- 未选择状态 -->
          <div v-if="!male" class="placeholder-area" @click="openSelect(1)">
            <div class="placeholder-box">
              <a-icon type="search" class="placeholder-icon" />
              <span class="placeholder-text">请先选择目标个体</span>
            </div>
          </div>
          
          <!-- 已选择状态 -->
          <template v-else>
            <item-card :title="male.vid" :item="male" @click.native="viewItemInfo(male)"></item-card>
            <div v-if="finish_query" class="chart-area">
              <h4 class="chart-title">家系图谱</h4>
              <item-tree-chart ref="maleChart"></item-tree-chart>
            </div>
          </template>
        </div>
      </div>

      <!-- 母畜选择卡片 -->
      <div class="selection-card">
        <div class="card-header">
          <h3 class="card-title">
            <span class="gender-icon female">♀</span>
            选择母畜个体
          </h3>
          <a-button type="primary" class="select-btn" @click="openSelect(0)">
            <a-icon type="plus" />
            选择个体
          </a-button>
        </div>
        
        <div class="card-content">
          <!-- 未选择状态 -->
          <div v-if="!female" class="placeholder-area" @click="openSelect(0)">
            <div class="placeholder-box">
              <a-icon type="search" class="placeholder-icon" />
              <span class="placeholder-text">请先选择目标个体</span>
            </div>
          </div>
          
          <!-- 已选择状态 -->
          <template v-else>
            <item-card :title="female.vid" :item="female" @click.native="viewItemInfo(female)"></item-card>
            <div v-if="finish_query" class="chart-area">
              <h4 class="chart-title">家系图谱</h4>
              <item-tree-chart ref="femaleChart"></item-tree-chart>
            </div>
          </template>
        </div>
      </div>
    </div>

    <!-- 底部操作按钮 -->
    <div class="action-bar">
      <div v-if="predicted_offspring_f !== null && finish_query" class="inbreeding-info">
        <span class="inbreeding-label">预测后代近交系数：</span>
        <span :style="{ color: getInbreedingColor(predicted_offspring_f) }" class="inbreeding-value">
          {{ formatInbreeding(predicted_offspring_f) }}
        </span>
      </div>
      <a-button class="cancel-btn" @click="handleCancel">取消</a-button>
      <a-button 
        type="primary" 
        class="confirm-btn"
        :disabled="!canConfirm"
        :loading="loading"
        @click="get_result"
      >
        确认匹配
      </a-button>
    </div>

    <!-- 选择弹窗 -->
    <item-select ref="itemSelect" @select-over="onSelectOver"></item-select>
  </div>
</template>

<script>
import itemSelect from './itemSelect.vue';
import itemTreeChart from './itemTreeChart.vue';
import itemCard from '../../breed_items/components/itemCard.vue';

export default {
  name: 'SingleYuZhong',
  components: {
    itemSelect,
    itemTreeChart,
    itemCard
  },
  
  data() {
    return {
      male: null,
      female: null,
      finish_query: false,
      loading: false,
      predicted_offspring_f: null
    };
  },
  
  computed: {
    canConfirm() {
      return this.male && this.female;
    }
  },
  
  methods: {
    openSelect(gender) {
      this.$refs.itemSelect.open({
        gender: gender
      });
    },
    
    onSelectOver(item) {
      if (item.gender === 1) {
        this.male = item;
      } else {
        this.female = item;
      }
      // 选择新个体时重置图谱
      this.finish_query = false;
    },
    
    async get_result() {
      if (!this.canConfirm) return;
      
      this.loading = true;
      try {
        const res = await this.$api.get_yu_zhong_result({
          male_id: this.male.id,
          female_id: this.female.id
        });
        
        this.finish_query = true;
        this.predicted_offspring_f = res.data.predicted_offspring_f ?? null;
        
        this.$nextTick(() => {
          if (this.$refs.maleChart) {
            this.$refs.maleChart.draw(res.data.male_chart_data);
          }
          if (this.$refs.femaleChart) {
            this.$refs.femaleChart.draw(res.data.female_chart_data);
          }
        });
      } catch (error) {
        this.$message.error('获取匹配结果失败');
        console.error('[SingleYuZhong] 获取匹配结果失败:', error);
      } finally {
        this.loading = false;
      }
    },
    
    handleCancel() {
      this.male = null;
      this.female = null;
      this.finish_query = false;
      this.predicted_offspring_f = null;
    },
    
    viewItemInfo(item) {
      this.$router.push({
        path: `/breed_items/${item.id}`
      }).catch(() => {});
    },
    
    getInbreedingColor(val) {
      if (val === null || val === undefined) return '#999';
      const v = Number(val);
      if (v === 0) return '#52c41a';      // 绿色 - 无近交
      if (v <= 0.0625) return '#52c41a';  // 绿色 - 低风险
      if (v <= 0.125) return '#faad14';   // 黄色 - 中风险
      return '#f5222d';                    // 红色 - 高风险
    },
    formatInbreeding(val) {
      if (val === null || val === undefined) return '待计算';
      return Number(val).toFixed(4);
    }
  }
};
</script>

<style lang="scss" scoped>
.single-matching-page {
  padding: 24px;
  min-height: 100%;
  background: #F9FAFB;
}

// 页面标题
.page-header {
  margin-bottom: 24px;
}

.page-title {
  font-size: 24px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 8px 0;
  font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

.page-subtitle {
  font-size: 14px;
  color: #6B7280;
  margin: 0;
  font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

// 选择卡片区域
.selection-cards {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-bottom: 24px;
}

.selection-card {
  background: #FFFFFF;
  border-radius: 16px;
  border: 1px solid #E5E7EB;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #F3F4F6;
}

.card-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
  color: #111827;
  margin: 0;
  font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

.gender-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  font-size: 16px;
  font-weight: 700;

  &.male {
    background: #EFF6FF;
    color: #2563EB;
  }

  &.female {
    background: #FDF2F8;
    color: #DB2777;
  }
}

.select-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  border-radius: 8px;
  font-weight: 500;
  font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

.card-content {
  padding: 24px;
  min-height: 200px;
}

// 占位区域
.placeholder-area {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
}

.placeholder-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 40px;
  border: 2px dashed #D1D5DB;
  border-radius: 12px;
  width: 100%;
  max-width: 400px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #2563EB;
    .placeholder-icon {
      color: #2563EB;
    }
    .placeholder-text {
      color: #2563EB;
    }
  }
}

.placeholder-icon {
  font-size: 32px;
  color: #9CA3AF;
}

.placeholder-text {
  font-size: 14px;
  color: #6B7280;
  font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

// 图谱区域
.chart-area {
  margin-top: 24px;
}

.chart-title {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin: 0 0 16px 0;
  font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

// 底部操作栏
.action-bar {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  padding: 20px 24px;
  background: #FFFFFF;
  border-radius: 16px;
  border: 1px solid #E5E7EB;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06);
}

.inbreeding-info {
  margin-right: auto;
  display: flex;
  align-items: center;
  gap: 6px;
}

.inbreeding-label {
  font-size: 14px;
  color: #6B7280;
  font-weight: 500;
}

.inbreeding-value {
  font-size: 18px;
  font-weight: 700;
  font-family: 'Courier New', monospace;
}

.cancel-btn {
  min-width: 88px;
  border-radius: 8px;
  font-weight: 500;
  font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

.confirm-btn {
  min-width: 104px;
  border-radius: 8px;
  font-weight: 500;
  font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

// 响应式布局
@media (max-width: 1024px) {
  .selection-cards {
    grid-template-columns: 1fr;
  }
}
</style>
